import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
  asPreviewLegalHtml,
  type PreviewLegalHtml,
} from '@/components/legal/LegalHtml'
import type { LegalDocumentKind } from '@/hooks/useLegalDocument'

export type { LegalDocumentKind }

export type LegalContentWarningCode =
  | 'raw_html_ignored'
  | 'disallowed_tag_removed'
  | 'disallowed_attribute_removed'
  | 'unsafe_link_removed'
  | 'previous_host_marker'
  | 'empty_content'

export type LegalContentWarningPath =
  | 'markdown'
  | 'markdown.image'
  | 'html'
  | 'html.attribute'
  | 'href'

export type LegalContentWarning = {
  code: LegalContentWarningCode
  path: LegalContentWarningPath
}

export type AdminLegalDocument = {
  id: string
  kind: LegalDocumentKind
  slug: string
  title: string
  currentPublishedRevisionId: string | null
}

export type AdminLegalDraft = {
  id: string
  documentId: string
  title: string
  sourceMarkdown: string
  sanitizedHtml: PreviewLegalHtml
  sanitizerWarnings: LegalContentWarning[]
  effectiveAt: string | null
  version: number
  updatedAt: string | null
}

export type AdminLegalRevision = {
  id: string
  documentId: string
  revisionNumber: number
  title: string
  sourceMarkdown: string
  sanitizedHtml: PreviewLegalHtml
  changeNote: string
  effectiveAt: string | null
  publishedAt: string | null
}

export type AdminLegalDocumentDetail = {
  document: AdminLegalDocument
  draft: AdminLegalDraft | null
  currentPublished: AdminLegalRevision | null
  revisions: AdminLegalRevision[]
}

export type LegalPreviewResult = {
  sanitizedHtml: PreviewLegalHtml
  warnings: LegalContentWarning[]
}

type DraftApi = Omit<AdminLegalDraft, 'sanitizedHtml'> & {
  sanitizedHtml: string
}

type RevisionApi = Omit<AdminLegalRevision, 'sanitizedHtml'> & {
  sanitizedHtml: string
}

type DetailApi = {
  document: AdminLegalDocument
  draft: DraftApi | null
  currentPublished: RevisionApi | null
  revisions: RevisionApi[]
}

type PreviewApi = {
  sanitizedHtml: string
  warnings: LegalContentWarning[]
}

type SaveDraftInput = {
  title: string
  sourceMarkdown: string
  effectiveAt: string | null
  version?: number
}

type PublishInput = {
  draftVersion: number
  changeNote: string
  effectiveAt: string | null
  confirmPastEffectiveAt?: boolean
}

const adminLegalContentQueryKey = (kind: LegalDocumentKind) =>
  ['admin', 'legal-content', kind] as const

function mapPreview(response: PreviewApi): LegalPreviewResult {
  return {
    sanitizedHtml: asPreviewLegalHtml(response.sanitizedHtml),
    warnings: response.warnings ?? [],
  }
}

function mapDraft(draft: DraftApi | null): AdminLegalDraft | null {
  if (!draft) return null
  return {
    ...draft,
    sanitizedHtml: asPreviewLegalHtml(draft.sanitizedHtml),
    sanitizerWarnings: draft.sanitizerWarnings ?? [],
  }
}

function mapRevision(revision: RevisionApi | null): AdminLegalRevision | null {
  if (!revision) return null
  return {
    ...revision,
    sanitizedHtml: asPreviewLegalHtml(revision.sanitizedHtml),
  }
}

function mapDetail(response: DetailApi): AdminLegalDocumentDetail {
  return {
    document: response.document,
    draft: mapDraft(response.draft),
    currentPublished: mapRevision(response.currentPublished),
    revisions: response.revisions.map((revision) => mapRevision(revision)!),
  }
}

export function useAdminLegalDocument(kind: LegalDocumentKind) {
  return useQuery({
    queryKey: adminLegalContentQueryKey(kind),
    queryFn: async () =>
      mapDetail(await api.get<DetailApi>(`/admin/legal-content/${kind}`)),
    retry: false,
  })
}

export function usePreviewLegalContent(kind: LegalDocumentKind) {
  return useMutation({
    mutationFn: async (sourceMarkdown: string) =>
      mapPreview(
        await api.post<PreviewApi>(`/admin/legal-content/${kind}/preview`, {
          sourceMarkdown,
        }),
      ),
  })
}

export function useSaveLegalDraft(kind: LegalDocumentKind) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SaveDraftInput) => {
      const response = await api.put<{ draft: DraftApi }>(
        `/admin/legal-content/${kind}/draft`,
        input,
      )
      return { draft: mapDraft(response.draft)! }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminLegalContentQueryKey(kind),
      })
    },
  })
}

export function usePublishLegalDraft(kind: LegalDocumentKind) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PublishInput) =>
      api.post<{ published: { id: string; revisionNumber: number } }>(
        `/admin/legal-content/${kind}/publish`,
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminLegalContentQueryKey(kind),
      })
    },
  })
}

export function useRestoreLegalRevision(kind: LegalDocumentKind) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (revisionId: string) => {
      const response = await api.post<{ draft: DraftApi }>(
        `/admin/legal-content/${kind}/revisions/${revisionId}/restore`,
        {},
      )
      return { draft: mapDraft(response.draft)! }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminLegalContentQueryKey(kind),
      })
    },
  })
}
